"use client";

import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  LoaderCircle,
  TriangleAlert,
} from "lucide-react";
import LinkWithPersistentQuery from "@components/LinkWithQuery/LinkWithQuery";
import { cn } from "@lib/utils";
import {
  ACCOUNT_HEALTH_STATUS_COLOR_CLASSES,
  type AccountHealthIssue,
  getAccountHealthStatus,
  useAccountHealth,
} from "@lib/hooks/use-account-health";
import { useFeatureFlag } from "@lib/hooks/useFeatureFlags";
import { Tooltip, TooltipContent, TooltipTrigger } from "@tracify/ui/tooltip";
import { usePostHog } from "posthog-js/react";

const statusPillClasses = {
  loading: "border-border bg-muted/60",
  critical: "border-destructive/30 bg-destructive/10",
  attention: "border-orange-500/30 bg-orange-500/10",
  warning: "border-yellow-500/30 bg-yellow-500/10",
  healthy: "border-emerald-500/30 bg-emerald-500/10",
} as const;

function HealthGauge({
  score,
  size = "small",
}: {
  score: number | null;
  size?: "small" | "large";
}) {
  const dimension = size === "large" ? "h-11 w-11" : "h-7 w-7";
  const strokeWidth = size === "large" ? 3.5 : 3;
  const radius = size === "large" ? 16 : 11;
  const center = size === "large" ? 22 : 14;
  const viewBoxSize = size === "large" ? 44 : 28;
  const displayedScore = score ?? 0;

  return (
    <span className={cn("relative inline-flex shrink-0", dimension)}>
      <svg
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="h-full w-full -rotate-[225deg]"
        aria-hidden="true"
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          pathLength="100"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray="75 25"
          className="opacity-20"
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          pathLength="100"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${displayedScore * 0.75} 100`}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[8px] font-semibold tabular-nums">
        {score === null ? (
          <LoaderCircle className="h-3 w-3 animate-spin" />
        ) : (
          score
        )}
      </span>
    </span>
  );
}

function IssueGroup({
  label,
  detail,
  issues,
}: {
  label: string;
  detail: string;
  issues: AccountHealthIssue[];
}) {
  if (issues.length === 0) return null;

  const isCritical = issues[0]?.severity === "critical";
  const Icon = isCritical ? AlertCircle : TriangleAlert;
  const visibleIssues = issues.slice(0, 3);

  return (
    <div className="py-2.5 first:pt-0 last:pb-0">
      <div className="flex items-center gap-1.5">
        <Icon
          className={cn(
            "h-3.5 w-3.5 shrink-0",
            isCritical ? "text-destructive" : "text-amber-500"
          )}
        />
        <span
          className={cn(
            "text-xs font-semibold",
            isCritical
              ? "text-destructive"
              : "text-amber-600 dark:text-amber-400"
          )}
        >
          {label}
        </span>
        <span className="text-xs text-muted-foreground">— {detail}</span>
      </div>
      <ul className="mt-1.5 space-y-1 pl-5 text-xs leading-4 text-muted-foreground">
        {visibleIssues.map((issue) => (
          <li key={issue.id}>{issue.title}</li>
        ))}
        {issues.length > visibleIssues.length ? (
          <li>+{issues.length - visibleIssues.length} more</li>
        ) : null}
      </ul>
    </div>
  );
}

function AccountHealthIndicatorContent() {
  const { issues, healthScore } = useAccountHealth();
  const posthog = usePostHog();
  const status = getAccountHealthStatus(healthScore, issues);
  const statusColor = ACCOUNT_HEALTH_STATUS_COLOR_CLASSES[status.variant];
  const criticalIssues = issues.filter(
    (issue) => issue.severity === "critical"
  );
  const warningIssues = issues.filter((issue) => issue.severity === "warning");
  const trackAccountHealthVisit = () => {
    posthog?.capture("Account health score link clicked", {
      source: "dashboard_header",
      health_score: healthScore,
      health_status: status.variant,
      critical_issue_count: criticalIssues.length,
      warning_issue_count: warningIssues.length,
    });
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <LinkWithPersistentQuery
          href="/settings/account-health"
          className={cn(
            "ml-auto inline-flex shrink-0 items-center gap-2 rounded-full border py-1 pl-1 pr-3 transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:hover:brightness-110",
            statusColor,
            statusPillClasses[status.variant]
          )}
          onClick={trackAccountHealthVisit}
          aria-label={`Account health: ${status.label}${
            healthScore === null ? "" : `, score ${healthScore} out of 100`
          }`}
        >
          <HealthGauge score={healthScore} />
          <span className="hidden text-xs font-semibold sm:inline">
            Account health
          </span>
        </LinkWithPersistentQuery>
      </TooltipTrigger>

      <TooltipContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="w-[min(20rem,calc(100vw-2rem))] p-4 text-left"
      >
        <div className="flex items-center gap-3 pb-3">
          <span className={statusColor}>
            <HealthGauge score={healthScore} size="large" />
          </span>
          <div className="min-w-0">
            <p
              className={cn(
                "text-[11px] font-semibold uppercase tracking-wider",
                statusColor
              )}
            >
              {status.label}
            </p>
            <p className="font-semibold text-foreground">
              Overall Health Score
            </p>
          </div>
        </div>

        <div className="border-t border-border py-1">
          {healthScore === null ? (
            <p className="py-2 text-xs leading-5 text-muted-foreground">
              {status.description}
            </p>
          ) : issues.length === 0 ? (
            <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
              All monitored checks are passing.
            </div>
          ) : (
            <div className="divide-y divide-border">
              <IssueGroup
                label="Critical"
                detail="blocking accurate reporting"
                issues={criticalIssues}
              />
              <IssueGroup
                label="Warnings"
                detail="affecting data quality"
                issues={warningIssues}
              />
            </div>
          )}
        </div>

        <LinkWithPersistentQuery
          href="/settings/account-health"
          className="mt-2 flex items-center gap-1 border-t border-border pt-3 text-xs font-semibold text-primary hover:underline"
          onClick={trackAccountHealthVisit}
        >
          View account health
          <ArrowRight className="h-3.5 w-3.5" />
        </LinkWithPersistentQuery>
      </TooltipContent>
    </Tooltip>
  );
}

export function AccountHealthIndicator() {
  const isEnabled = useFeatureFlag("health_dashboard");

  return isEnabled ? <AccountHealthIndicatorContent /> : null;
}
